import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Phone, Clock, AlertTriangle, User, ChevronRight,
  PhoneCall, PhoneOff, Calendar, CheckCircle, XCircle,
  ArrowRight, RotateCcw, Plus, Filter, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import LeadModal from '@/components/LeadModal';

import { API_URL } from '@/config/api';

const PRIORITY_STYLES = {
  urgent: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-800' },
  high: { bg: 'bg-orange-50 border-orange-200', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-800' },
  normal: { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-800' },
  low: { bg: 'bg-slate-50 border-slate-200', text: 'text-slate-600', badge: 'bg-slate-100 text-slate-700' },
};

const ManualQueuePage = () => {
  const { user, selectedBusiness, selectedCampaign } = useAuth();
  const [activeTab, setActiveTab] = useState('queue');
  const [queueLeads, setQueueLeads] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [todayCallbacks, setTodayCallbacks] = useState({ today: [], overdue: [], total_today: 0, total_overdue: 0 });
  const [callbackConfig, setCallbackConfig] = useState({ outcomes: [], priorities: [], escalation_configs: {} });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showOutcomeDialog, setShowOutcomeDialog] = useState(false);
  const [showCreateCallback, setShowCreateCallback] = useState(false);
  const [activeCallback, setActiveCallback] = useState(null);
  const [outcomeForm, setOutcomeForm] = useState({ outcome: '', notes: '', next_callback_date: '', next_callback_time: '' });
  const [createForm, setCreateForm] = useState({ lead_id: '', due_date: '', due_time: '', priority: 'normal', reason: '', notes: '' });
  const [reasonFilter, setReasonFilter] = useState('all');

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, []);

  const businessId = selectedCampaign?.business_id || (user?.role === 'super_admin' ? selectedBusiness?.business_id : user?.business_id);

  const fetchAll = useCallback(async () => {
    if (!businessId) return;
    setIsLoading(true);
    try {
      const headers = getHeaders();
      const bParam = `business_id=${businessId}`;
      const campaignParam = selectedCampaign?.campaign_id ? `&campaign_id=${selectedCampaign.campaign_id}` : '';
      const reasonParam = reasonFilter !== 'all' ? `&reason=${reasonFilter}` : '';

      const [queueRes, callbacksRes, todayRes, configRes] = await Promise.all([
        fetch(`${API_URL}/api/leads/queue/manual-followup?${bParam}${campaignParam}${reasonParam}`, { headers }),
        fetch(`${API_URL}/api/callbacks?status=pending`, { headers }),
        fetch(`${API_URL}/api/callbacks/today`, { headers }),
        fetch(`${API_URL}/api/callbacks/config`, { headers }),
      ]);

      if (queueRes.ok) setQueueLeads(await queueRes.json());
      if (callbacksRes.ok) setCallbacks(await callbacksRes.json());
      if (todayRes.ok) setTodayCallbacks(await todayRes.json());
      if (configRes.ok) setCallbackConfig(await configRes.json());
    } catch (err) {
      console.error('Error fetching queue data:', err);
      toast.error('Failed to load queue data');
    } finally {
      setIsLoading(false);
    }
  }, [businessId, reasonFilter, getHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCompleteCallback = (cb) => {
    setActiveCallback(cb);
    setOutcomeForm({ outcome: '', notes: '', next_callback_date: '', next_callback_time: '' });
    setShowOutcomeDialog(true);
  };

  const submitOutcome = async () => {
    if (!outcomeForm.outcome) { toast.error('Select an outcome'); return; }
    try {
      const res = await fetch(`${API_URL}/api/callbacks/${activeCallback.callback_id}/complete`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify(outcomeForm),
      });
      if (res.ok) {
        toast.success('Callback completed');
        setShowOutcomeDialog(false);
        fetchAll();
      } else {
        toast.error('Failed to complete callback');
      }
    } catch { toast.error('Error completing callback'); }
  };

  const handleCancelCallback = async (cbId) => {
    try {
      const res = await fetch(`${API_URL}/api/callbacks/${cbId}`, { method: 'DELETE', headers: getHeaders() });
      if (res.ok) { toast.success('Callback cancelled'); fetchAll(); }
    } catch { toast.error('Error cancelling callback'); }
  };

  const submitCreateCallback = async () => {
    if (!createForm.lead_id || !createForm.due_date || !createForm.reason) {
      toast.error('Fill in lead, date, and reason'); return;
    }
    try {
      const res = await fetch(`${API_URL}/api/callbacks`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify(createForm),
      });
      if (res.ok) {
        toast.success('Callback created');
        setShowCreateCallback(false);
        setCreateForm({ lead_id: '', due_date: '', due_time: '', priority: 'normal', reason: '', notes: '' });
        fetchAll();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create callback');
      }
    } catch { toast.error('Error creating callback'); }
  };

  const openLeadModal = (lead) => { setSelectedLead(lead); setShowLeadModal(true); };

  const escalationConfigs = callbackConfig.escalation_configs || {};
  const escalationReasons = Object.entries(escalationConfigs).map(([k, v]) => ({ value: k, label: v.label }));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="queue-loading">
        <div className="w-8 h-8 border-4 border-[#00B4D8] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const needsReschedule = (outcome) => {
    const cfg = callbackConfig.outcomes?.find(o => o.value === outcome);
    return cfg?.next_action === 'reschedule';
  };

  return (
    <div className="space-y-6" data-testid="manual-queue-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800" data-testid="queue-title">Manual Queue</h1>
          <p className="text-slate-500 mt-1">AI escalations, callbacks, and manual follow-ups</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => fetchAll()} data-testid="refresh-queue-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button onClick={() => setShowCreateCallback(true)} data-testid="create-callback-btn">
            <Plus className="w-4 h-4 mr-2" /> New Callback
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card data-testid="stat-queue-count">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100"><AlertTriangle className="w-5 h-5 text-amber-600" /></div>
              <div>
                <p className="text-sm text-slate-500">In Queue</p>
                <p className="text-2xl font-bold text-slate-800">{queueLeads.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-callbacks-today">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100"><Phone className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Due Today</p>
                <p className="text-2xl font-bold text-slate-800">{todayCallbacks.total_today}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-overdue">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100"><Clock className="w-5 h-5 text-red-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Overdue</p>
                <p className="text-2xl font-bold text-red-600">{todayCallbacks.total_overdue}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-pending-callbacks">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-100"><PhoneCall className="w-5 h-5 text-cyan-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Pending Callbacks</p>
                <p className="text-2xl font-bold text-slate-800">{callbacks.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="queue-tabs">
          <TabsTrigger value="queue" data-testid="tab-queue">
            Escalation Queue ({queueLeads.length})
          </TabsTrigger>
          <TabsTrigger value="today" data-testid="tab-today">
            Today's Calls ({todayCallbacks.total_today + todayCallbacks.total_overdue})
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all-callbacks">
            All Callbacks ({callbacks.length})
          </TabsTrigger>
        </TabsList>

        {/* ESCALATION QUEUE TAB */}
        <TabsContent value="queue" className="mt-4">
          <div className="flex items-center gap-3 mb-4">
            <Filter className="w-4 h-4 text-slate-400" />
            <Select value={reasonFilter} onValueChange={setReasonFilter}>
              <SelectTrigger className="w-[220px]" data-testid="reason-filter">
                <SelectValue placeholder="Filter by reason" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Reasons</SelectItem>
                {escalationReasons.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {queueLeads.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-slate-700">Queue is clear</h3>
                <p className="text-slate-500 mt-1">No leads need manual follow-up right now</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {queueLeads.map(lead => {
                const reason = lead.manual_followup_reason || 'escalation';
                const config = escalationConfigs[reason] || {};
                const prio = config.priority || 'normal';
                const style = PRIORITY_STYLES[prio] || PRIORITY_STYLES.normal;
                return (
                  <Card
                    key={lead.lead_id}
                    className={`border ${style.bg} cursor-pointer hover:shadow-md transition-shadow`}
                    data-testid={`queue-lead-${lead.lead_id}`}
                    onClick={() => openLeadModal(lead)}
                  >
                    <CardContent className="py-3 px-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-full ${style.badge}`}>
                            <User className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-slate-800">{lead.name || 'Unknown'}</span>
                              <Badge className={style.badge}>{config.label || reason}</Badge>
                              <Badge variant="outline" className={style.text}>{prio}</Badge>
                              {lead.status === 'callback' && lead.callback_date && (
                                <Badge className="bg-green-100 text-green-700 border-green-200">
                                  <Calendar className="w-3 h-3 mr-1" />
                                  Callback {lead.callback_date.split('T')[0]}
                                </Badge>
                              )}
                              {lead.status === 'callback' && !lead.callback_date && (
                                <Badge className="bg-green-100 text-green-700 border-green-200">
                                  <CheckCircle className="w-3 h-3 mr-1" /> Dealt with
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                              {lead.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</span>}
                              {lead.email && <span>{lead.email}</span>}
                              {lead.ai_status && <span className="text-xs">AI: {lead.ai_status}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm" variant="outline"
                            data-testid={`call-lead-${lead.lead_id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (lead.phone) window.open(`tel:${lead.phone}`);
                            }}
                          >
                            <PhoneCall className="w-4 h-4 mr-1" /> Call
                          </Button>
                          <Button
                            size="sm"
                            data-testid={`create-cb-${lead.lead_id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setCreateForm(f => ({ ...f, lead_id: lead.lead_id, reason: config.label || reason }));
                              setShowCreateCallback(true);
                            }}
                          >
                            <Calendar className="w-4 h-4 mr-1" /> Schedule
                          </Button>
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* TODAY'S CALLS TAB */}
        <TabsContent value="today" className="mt-4 space-y-6">
          {todayCallbacks.total_overdue > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-red-600 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" /> Overdue ({todayCallbacks.total_overdue})
              </h3>
              <div className="space-y-2">
                {todayCallbacks.overdue.map(cb => (
                  <CallbackCard
                    key={cb.callback_id} cb={cb}
                    onComplete={handleCompleteCallback}
                    onCancel={handleCancelCallback}
                    isOverdue
                  />
                ))}
              </div>
            </div>
          )}
          <div>
            <h3 className="text-lg font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Phone className="w-5 h-5" /> Due Today ({todayCallbacks.total_today})
            </h3>
            {todayCallbacks.total_today === 0 && todayCallbacks.total_overdue === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-slate-700">No calls due today</h3>
                  <p className="text-slate-500 mt-1">You're all caught up</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {todayCallbacks.today.map(cb => (
                  <CallbackCard key={cb.callback_id} cb={cb} onComplete={handleCompleteCallback} onCancel={handleCancelCallback} />
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ALL CALLBACKS TAB */}
        <TabsContent value="all" className="mt-4">
          {callbacks.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Phone className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-slate-700">No pending callbacks</h3>
                <p className="text-slate-500 mt-1">Create a callback from the escalation queue or use the button above</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {callbacks.map(cb => (
                <CallbackCard key={cb.callback_id} cb={cb} onComplete={handleCompleteCallback} onCancel={handleCancelCallback} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Outcome Dialog */}
      <Dialog open={showOutcomeDialog} onOpenChange={setShowOutcomeDialog}>
        <DialogContent data-testid="outcome-dialog" aria-describedby="outcome-dialog-desc">
          <DialogHeader>
            <DialogTitle>Log Call Outcome</DialogTitle>
            <p id="outcome-dialog-desc" className="text-sm text-slate-500">Record the result of this callback</p>
          </DialogHeader>
          {activeCallback && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-medium">{activeCallback.lead_name || 'Unknown Lead'}</p>
                <p className="text-sm text-slate-500">{activeCallback.lead_phone} - {activeCallback.reason}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Outcome</label>
                <Select value={outcomeForm.outcome} onValueChange={v => setOutcomeForm(f => ({ ...f, outcome: v }))}>
                  <SelectTrigger data-testid="outcome-select">
                    <SelectValue placeholder="Select outcome" />
                  </SelectTrigger>
                  <SelectContent>
                    {callbackConfig.outcomes?.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Notes</label>
                <Textarea
                  data-testid="outcome-notes"
                  value={outcomeForm.notes}
                  onChange={e => setOutcomeForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="What happened on the call?"
                  rows={3}
                />
              </div>
              {needsReschedule(outcomeForm.outcome) && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Next Callback Date</label>
                    <Input
                      type="date" data-testid="next-callback-date"
                      value={outcomeForm.next_callback_date}
                      onChange={e => setOutcomeForm(f => ({ ...f, next_callback_date: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Time</label>
                    <Input
                      type="time" data-testid="next-callback-time"
                      value={outcomeForm.next_callback_time}
                      onChange={e => setOutcomeForm(f => ({ ...f, next_callback_time: e.target.value }))}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOutcomeDialog(false)}>Cancel</Button>
            <Button onClick={submitOutcome} data-testid="submit-outcome-btn">Save Outcome</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Callback Dialog */}
      <Dialog open={showCreateCallback} onOpenChange={setShowCreateCallback}>
        <DialogContent data-testid="create-callback-dialog" aria-describedby="create-callback-desc">
          <DialogHeader>
            <DialogTitle>Schedule Callback</DialogTitle>
            <p id="create-callback-desc" className="text-sm text-slate-500">Set up a callback task for a lead</p>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Lead ID</label>
              <Input
                data-testid="cb-lead-id"
                value={createForm.lead_id}
                onChange={e => setCreateForm(f => ({ ...f, lead_id: e.target.value }))}
                placeholder="Enter lead ID"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Date</label>
                <Input
                  type="date" data-testid="cb-date"
                  value={createForm.due_date}
                  onChange={e => setCreateForm(f => ({ ...f, due_date: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Time</label>
                <Input
                  type="time" data-testid="cb-time"
                  value={createForm.due_time}
                  onChange={e => setCreateForm(f => ({ ...f, due_time: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Priority</label>
              <Select value={createForm.priority} onValueChange={v => setCreateForm(f => ({ ...f, priority: v }))}>
                <SelectTrigger data-testid="cb-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {callbackConfig.priorities?.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Reason</label>
              <Input
                data-testid="cb-reason"
                value={createForm.reason}
                onChange={e => setCreateForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="Why is this callback needed?"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Notes</label>
              <Textarea
                data-testid="cb-notes"
                value={createForm.notes}
                onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Additional notes"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateCallback(false)}>Cancel</Button>
            <Button onClick={submitCreateCallback} data-testid="submit-callback-btn">Create Callback</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead Modal */}
      {showLeadModal && selectedLead && (
        <LeadModal
          isOpen={showLeadModal}
          lead={selectedLead}
          onClose={() => { setShowLeadModal(false); setSelectedLead(null); }}
          onUpdate={() => { fetchAll(); setShowLeadModal(false); setSelectedLead(null); }}
        />
      )}
    </div>
  );
};

/* Callback Card Component */
const CallbackCard = ({ cb, onComplete, onCancel, isOverdue = false }) => {
  const prio = cb.priority || 'normal';
  const style = PRIORITY_STYLES[prio] || PRIORITY_STYLES.normal;

  return (
    <Card className={`border ${isOverdue ? 'bg-red-50 border-red-200' : style.bg}`} data-testid={`callback-${cb.callback_id}`}>
      <CardContent className="py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`p-2 rounded-full ${isOverdue ? 'bg-red-100 text-red-700' : style.badge}`}>
              <PhoneCall className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-800">{cb.lead_name || 'Unknown'}</span>
                <Badge className={style.badge}>{prio}</Badge>
                {isOverdue && <Badge className="bg-red-100 text-red-800">Overdue</Badge>}
              </div>
              <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                {cb.lead_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{cb.lead_phone}</span>}
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{cb.due_date} {cb.due_time || ''}</span>
                <span>{cb.reason}</span>
              </div>
              {cb.notes && <p className="text-xs text-slate-400 mt-1">{cb.notes}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {cb.lead_phone && (
              <Button size="sm" variant="outline" onClick={() => window.open(`tel:${cb.lead_phone}`)}>
                <Phone className="w-4 h-4" />
              </Button>
            )}
            <Button size="sm" onClick={() => onComplete(cb)} data-testid={`complete-cb-${cb.callback_id}`}>
              <CheckCircle className="w-4 h-4 mr-1" /> Log Outcome
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onCancel(cb.callback_id)} data-testid={`cancel-cb-${cb.callback_id}`}>
              <XCircle className="w-4 h-4 text-slate-400" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ManualQueuePage;
